import { useEffect, useState } from "react";
import type { MachineSummary } from "@racler/protocol";
import { useRelay } from "./relayContext.tsx";

/** Machine list with live presence (system/machine.status events). */
export function useMachines(): {
  machines: MachineSummary[] | null;
  error: string | null;
  /** Re-read the list from the server (e.g. after a revocation). */
  reload: () => void;
} {
  const relay = useRelay();
  const [machines, setMachines] = useState<MachineSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      relay
        .server("system/machines.list", {})
        .then((r) => {
          if (!cancelled) {
            setMachines(r.machines);
            setError(null);
          }
        })
        .catch((e: unknown) => !cancelled && setError(String((e as Error).message)));
    };
    const offState = relay.onState((s) => s === "open" && load());
    if (relay.connectionState === "open") load();
    const offEvents = relay.on((msg) => {
      if (msg.channel !== "system" || msg.type !== "machine.status") return;
      const { machineId, online } = msg.payload as { machineId: string; online: boolean };
      setMachines((prev) => {
        if (!prev) return prev;
        if (!prev.some((m) => m.id === machineId)) {
          load(); // newly paired machine
          return prev;
        }
        return prev.map((m) => (m.id === machineId ? { ...m, online } : m));
      });
    });
    return () => {
      cancelled = true;
      offState();
      offEvents();
    };
  }, [relay, reloadKey]);

  return { machines, error, reload: () => setReloadKey((k) => k + 1) };
}
